import React, { useCallback, useEffect, useState } from 'react';
import { compose } from 'recompose';
import { withFormik } from 'formik';
import { bool, func, object, shape, string } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Button, CircularProgress } from '@mui/material';

import EmailDisplay from 'components/EmailDisplay';
import ErrorPanel from 'components/ErrorPanel';
import { passwordResetValidationSchema } from 'validationSchemas';
import { LOGIN_PASSWORD } from 'assets/Icons';
import { clearError, resetPasswordUser } from 'store/user/actions';
import { logoutUser } from 'store/auth/actions';
import { errorSelector, isLoadingSelector } from 'store/user/selectors';
import '../login-form.scss';

function ResetPasswordForm({ isValid, setFieldTouched, handleChange, touched, errors, values }) {
  const dispatch = useDispatch();
  const isLoading = useSelector(isLoadingSelector);
  const serverError = useSelector(errorSelector);
  const [showError, setShowError] = useState(true);
  const [unitedErrors, setUnitedErrors] = useState([]);

  const componentCleanup = useCallback(() => {
    dispatch(logoutUser());
    if (serverError) {
      dispatch(clearError());
    }
  }, [dispatch, serverError]);

  useEffect(() => {
    window.addEventListener('beforeunload', componentCleanup);

    return () => {
      window.removeEventListener('beforeunload', componentCleanup);
    };
  }, [componentCleanup]);

  useEffect(() => {
    setShowError(true);
  }, [serverError]);

  const closeErrorPanel = useCallback(() => {
    if (serverError) {
      dispatch(clearError());
    }
    setShowError(false);
    setUnitedErrors([]);
  }, [dispatch, serverError]);

  const onSubmit = (event) => {
    event.preventDefault();
    if (!isValid) {
      const nextErrors = [serverError];
      Object.keys(errors).forEach((key) => {
        if (touched[key]) {
          nextErrors.push(errors[key]);
        }
      });
      setShowError(true);
      setUnitedErrors(nextErrors);
      return;
    }

    dispatch(resetPasswordUser(values));
  };

  const handleInputChange = (event) => {
    if (serverError) {
      dispatch(clearError());
    }
    if (showError) {
      closeErrorPanel();
    }
    handleChange(event);
  };

  return (
    <div className="login-form-page--content">
      <div className="login-form">
        <h2 className="title reset-pwd">Set your new password</h2>
        <form onSubmit={onSubmit}>
          <EmailDisplay />
          {serverError ? (
            <ErrorPanel
              message={serverError}
              buttonClickHandler={closeErrorPanel}
              key={serverError}
              dataTestId="password-reset-server-error"
            />
          ) : (
            unitedErrors.map((error, index) =>
              error ? (
                <ErrorPanel
                  message={error}
                  buttonClickHandler={closeErrorPanel}
                  key={error}
                  dataTestId={`password-reset-validation-error-${index}`}
                />
              ) : null,
            )
          )}
          <div className="input-container">
            <div className="icon-container">
              <img src={LOGIN_PASSWORD} className="person-icon" alt="person-icon" />
            </div>
            <input
              type="password"
              value={values.password}
              name="password"
              placeholder="Password"
              required
              onChange={handleInputChange}
              onBlur={(evt) => setFieldTouched(evt.target.name)}
            />
          </div>
          <div className="input-container">
            <div className="icon-container">
              <img src={LOGIN_PASSWORD} className="person-icon" alt="person-icon" />
            </div>
            <input
              type="password"
              value={values.passwordConfirmation}
              name="passwordConfirmation"
              placeholder="Confirm Your Password"
              required
              onChange={handleInputChange}
              onBlur={(evt) => setFieldTouched(evt.target.name)}
            />
          </div>
          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={isLoading || !values.password || values.password !== values.passwordConfirmation}
            sx={{
              fontSize: 18,
              p: 2,
              bgcolor: '#4986c5',
              textTransform: 'unset',
              '&:hover': { bgcolor: '#4986c5' },
            }}
          >
            {isLoading ? <CircularProgress /> : 'Submit'}
          </Button>
        </form>
      </div>
    </div>
  );
}

ResetPasswordForm.propTypes = {
  isValid: bool.isRequired,
  setFieldTouched: func.isRequired,
  handleChange: func.isRequired,
  touched: object.isRequired,
  errors: object,
  values: shape({
    password: string,
    passwordConfirmation: string,
  }).isRequired,
};

export default compose(
  withFormik({
    mapPropsToValues: () => ({
      password: '',
      passwordConfirmation: '',
    }),
    validationSchema: passwordResetValidationSchema,
  }),
)(ResetPasswordForm);
